/** One run of text, flagged for whether it is a search hit. */
export interface ITextSegment {
  text: string;
  isMatch: boolean;
}

/**
 * Splits `text` into alternating non-matching and matching runs on every
 * case-insensitive occurrence of `needle`, left to right and non-overlapping.
 * Concatenating the segments reproduces `text` exactly, so a renderer can wrap
 * the matches without altering what is displayed.
 *
 * Matching is plain substring containment — not a regex — so `%`, `.`, `*` and
 * friends in an operator's query are literal characters. That is deliberately
 * the same rule the server's `strpos` search applies, so the UI highlights
 * exactly what the query matched.
 *
 * An empty needle, or one longer than the text, yields a single unmatched
 * segment.
 */
export function splitOnMatches(text: string, needle: string): ITextSegment[] {
  const whole: ITextSegment[] = [{ text, isMatch: false }];
  if (needle.length === 0 || needle.length > text.length) {
    return whole;
  }

  const haystack = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  // A few characters change length under `toLowerCase` (ẞ → ss, İ → i̇), which
  // would desync these indices from the original string and slice it wrongly.
  // Highlighting is a display aid, so drop it rather than corrupt the text.
  if (haystack.length !== text.length || lowerNeedle.length !== needle.length) {
    return whole;
  }

  const segments: ITextSegment[] = [];
  let cursor = 0;
  for (;;) {
    const at = haystack.indexOf(lowerNeedle, cursor);
    if (at === -1) {
      break;
    }
    if (at > cursor) {
      segments.push({ text: text.slice(cursor, at), isMatch: false });
    }
    segments.push({ text: text.slice(at, at + needle.length), isMatch: true });
    cursor = at + needle.length;
  }
  if (segments.length === 0) {
    return whole;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), isMatch: false });
  }
  return segments;
}

/** How many times `needle` occurs in `text`, by the same rule as {@link splitOnMatches}. */
export function countMatches(text: string, needle: string): number {
  return splitOnMatches(text, needle).reduce((total, segment) => total + (segment.isMatch ? 1 : 0), 0);
}

export default splitOnMatches;
